import { StandingDto } from '@shared/api/hooks/useStandings';

export type TieBreakMode = 'simple' | 'figc-serie-c' | 'figc-lnd';

export const TIEBREAK_LOCALSTORAGE_KEY = 'teramo.standings.tieBreakMode';

export interface SortableRow {
  position: number;
  teamName: string;
  played: number;
  won: number;
  drawn: number;
  lost: number;
  goalsFor: number;
  goalsAgainst: number;
  goalDifference: number;
  points: number;
  penalty: number;
  isOwnTeam: boolean;
  teamLogo: string | null;
}

export function netPoints(r: { won: number; drawn: number; penalty: number }): number {
  return 3 * r.won + r.drawn - r.penalty;
}

export function goalDifference(r: { goalsFor: number; goalsAgainst: number }): number {
  return r.goalsFor - r.goalsAgainst;
}

/**
 * Sort by points netto + tie-break per mode. NB: classifica avulsa (head-to-head)
 * non e' implementabile senza dati partita; tutte le modalita' FIGC fallback su
 * differenza reti + gol fatti generali. La UI mostra ⓘ tooltip sui gruppi pari merito.
 * Generic on T so callers can preserve their own row fields (uid, etc.).
 */
export function sortStandings<T extends SortableRow>(rows: T[], mode: TieBreakMode): T[] {
  const copy = [...rows];
  copy.sort((a, b) => {
    if (b.points !== a.points) return b.points - a.points;
    if (mode === 'simple') {
      if (b.won !== a.won) return b.won - a.won;
      return a.teamName.localeCompare(b.teamName);
    }
    if (b.goalDifference !== a.goalDifference) return b.goalDifference - a.goalDifference;
    if (b.goalsFor !== a.goalsFor) return b.goalsFor - a.goalsFor;
    return a.teamName.localeCompare(b.teamName);
  });
  return copy;
}

/**
 * Group teams that share identical primary tie-break keys (i.e. same `points` in any
 * mode + same wins in `simple`). UI uses this to show "scontri diretti non disponibili"
 * tooltips on tied groups in FIGC modes.
 */
export function findTiedGroups<T extends SortableRow>(rows: T[], mode: TieBreakMode): T[][] {
  const groups: T[][] = [];
  let current: T[] = [];
  for (let i = 0; i < rows.length; i++) {
    const r = rows[i];
    const prev = rows[i - 1];
    const isTie =
      prev !== undefined &&
      r.points === prev.points &&
      (mode !== 'simple' || r.won === prev.won);
    if (isTie) {
      if (current.length === 0) current.push(prev);
      current.push(r);
    } else if (current.length > 0) {
      groups.push(current);
      current = [];
    }
  }
  if (current.length > 0) groups.push(current);
  return groups;
}

export function loadTieBreakMode(competitionSeason: string): TieBreakMode {
  try {
    const raw = window.localStorage.getItem(`${TIEBREAK_LOCALSTORAGE_KEY}.${competitionSeason}`);
    if (raw === 'simple' || raw === 'figc-serie-c' || raw === 'figc-lnd') return raw;
  } catch {
    /* ignore (SSR / disabled storage) */
  }
  return 'simple';
}

export function persistTieBreakMode(competitionSeason: string, mode: TieBreakMode): void {
  try {
    window.localStorage.setItem(`${TIEBREAK_LOCALSTORAGE_KEY}.${competitionSeason}`, mode);
  } catch {
    /* ignore */
  }
}

export function rowToSortable(r: StandingDto): SortableRow {
  return {
    position: r.position,
    teamName: r.teamName,
    played: r.played,
    won: r.won,
    drawn: r.drawn,
    lost: r.lost,
    goalsFor: r.goalsFor,
    goalsAgainst: r.goalsAgainst,
    goalDifference: r.goalDifference,
    points: r.points,
    penalty: r.penalty,
    isOwnTeam: r.isOwnTeam,
    teamLogo: r.teamLogo,
  };
}
